import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  orderBy,
  where,
  Timestamp,
  type FieldValue,
} from "firebase/firestore";
import { db } from "./init";
import { convertTimestampToDate } from "./firestore";
import { Company } from "@/types/company";
import { buildPublicStorageUrl } from "@/utils/storageUtils";

/**
 * Firestore 製作公司數據類型（包含 Timestamp）
 */
interface FirestoreCompanyData {
  company_id?: string;
  company_name?: string;
  company_logo?: string;
  website?: string;
  description?: string;
  created_at?: Timestamp | Date | null;
  updated_at?: Timestamp | Date | null;
}

/**
 * 轉換製作公司數據（處理 Timestamp）
 */
function convertCompanyData(data: FirestoreCompanyData, id: string): Omit<Company, "id"> {
  return {
    company_id: data.company_id || id,
    company_name: data.company_name || "",
    company_logo: data.company_logo,
    website: data.website,
    description: data.description,
    created_at: convertTimestampToDate(data.created_at) as Date | undefined,
    updated_at: convertTimestampToDate(data.updated_at) as Date | undefined,
  };
}

/**
 * 獲取所有製作公司
 */
export async function getCompanies(): Promise<Company[]> {
  try {
    const companiesRef = collection(db, "companies");
    let querySnapshot;
    let useClientSort = false;

    try {
      const q = query(companiesRef, orderBy("company_name", "asc"));
      querySnapshot = await getDocs(q);
    } catch (orderError) {
      console.warn("Failed to order by 'company_name', using basic query with client-side sort:", orderError);
      querySnapshot = await getDocs(query(companiesRef));
      useClientSort = true;
    }

    const companies = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...convertCompanyData(doc.data(), doc.id),
    })) as Company[];

    if (useClientSort) {
      companies.sort((a, b) => (a.company_name || "").localeCompare(b.company_name || ""));
    }

    return companies;
  } catch (error) {
    console.error("Error getting companies:", error);
    throw error;
  }
}

/**
 * 獲取單個製作公司
 */
export async function getCompany(id: string): Promise<Company | null> {
  try {
    const companyRef = doc(db, "companies", id);
    const companySnap = await getDoc(companyRef);

    if (companySnap.exists()) {
      return {
        id: companySnap.id,
        ...convertCompanyData(companySnap.data(), companySnap.id),
      } as Company;
    }

    return null;
  } catch (error) {
    console.error("Error getting company:", error);
    throw error;
  }
}

/**
 * 檢查 company_id 是否已存在
 */
async function checkCompanyIdExists(companyId: string, excludeId?: string): Promise<boolean> {
  try {
    const companiesRef = collection(db, "companies");
    const q = query(companiesRef, where("company_id", "==", companyId));
    const querySnapshot = await getDocs(q);

    // 如果提供了 excludeId，排除當前文檔
    if (excludeId) {
      return querySnapshot.docs.some((doc) => doc.id !== excludeId);
    }

    return !querySnapshot.empty;
  } catch (error) {
    console.error("Error checking company_id:", error);
    throw error;
  }
}

/**
 * 創建製作公司
 */
export async function createCompany(
  data: Omit<Company, "id" | "created_at" | "updated_at">
): Promise<string> {
  try {
    if (!data.company_id || data.company_id.trim().length === 0) {
      throw new Error("公司 ID 為必填");
    }
    if (!data.company_name || data.company_name.trim().length === 0) {
      throw new Error("公司名稱為必填");
    }

    // 檢查 company_id 是否已存在
    const exists = await checkCompanyIdExists(data.company_id);
    if (exists) {
      throw new Error("此公司 ID 已存在");
    }

    const companiesRef = collection(db, "companies");
    const companyData: {
      company_id: string;
      company_name: string;
      company_logo?: string;
      website?: string;
      description?: string;
      created_at: Timestamp;
      updated_at: Timestamp;
    } = {
      company_id: data.company_id.trim(),
      company_name: data.company_name.trim(),
      created_at: Timestamp.now(),
      updated_at: Timestamp.now(),
    };

    if (data.company_logo) companyData.company_logo = data.company_logo;
    if (data.website) companyData.website = data.website;
    if (data.description) companyData.description = data.description;

    const docRef = doc(companiesRef);
    await setDoc(docRef, companyData);
    return docRef.id;
  } catch (error) {
    console.error("Error creating company:", error);
    throw error;
  }
}

/**
 * 更新製作公司
 */
export async function updateCompany(
  id: string,
  data: Partial<Omit<Company, "id" | "created_at">>
): Promise<void> {
  try {
    const companyRef = doc(db, "companies", id);

    // 如果更新 company_id，檢查是否已存在
    if (data.company_id !== undefined) {
      const exists = await checkCompanyIdExists(data.company_id, id);
      if (exists) {
        throw new Error("此公司 ID 已存在");
      }
    }

    const updateData: {
      updated_at: Timestamp;
      company_id?: string;
      company_name?: string;
      company_logo?: string | FieldValue;
      website?: string | FieldValue;
      description?: string | FieldValue;
    } = {
      updated_at: Timestamp.now(),
    };

    if (data.company_id !== undefined) updateData.company_id = data.company_id.trim();
    if (data.company_name !== undefined) updateData.company_name = data.company_name.trim();
    // 清空欄位 → 用 deleteField() 從文件移除（Firestore 不接受 undefined）
    if (data.company_logo !== undefined) {
      updateData.company_logo = data.company_logo ? data.company_logo : deleteField();
    }
    if (data.website !== undefined) {
      const trimmed = data.website?.trim();
      updateData.website = trimmed ? trimmed : deleteField();
    }
    if (data.description !== undefined) {
      const trimmed = data.description?.trim();
      updateData.description = trimmed ? trimmed : deleteField();
    }

    await updateDoc(companyRef, updateData);
  } catch (error) {
    console.error("Error updating company:", error);
    throw error;
  }
}

/**
 * 刪除製作公司
 */
export async function deleteCompany(id: string): Promise<void> {
  try {
    const companyRef = doc(db, "companies", id);
    await deleteDoc(companyRef);
  } catch (error) {
    console.error("Error deleting company:", error);
    throw error;
  }
}

/**
 * 同步建構製作公司 Logo URL
 *
 * companies bucket 為公開可讀(storage.rules: allow read: if true),
 * 直接組 CDN URL,無需呼叫 Firebase SDK getDownloadURL,瀏覽器自動 disk-cache。
 */
export function getCompanyLogoUrl(
  logoUrl: string | undefined,
  defaultPrefix: string = "company_logos"
): string {
  return buildPublicStorageUrl(logoUrl, defaultPrefix);
}

